import React, { useState, useRef } from 'react';
import { X, Plus, Trash2, Eye, AlertCircle, Upload, Image as ImageIcon, Video, FileText, Bold, Italic, Strikethrough, Code } from 'lucide-react';
import { WhatsAppTemplate, CreateTemplateRequest } from 'types';
import { whatsappService } from 'api/services/whatsapp.service';

interface TemplateCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (template: WhatsAppTemplate) => void;
  editTemplate?: WhatsAppTemplate | null;
}

type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
type HeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
type ButtonType = 'QUICK_REPLY' | 'CALL_TO_ACTION' | 'URL';

interface TemplateComponent {
  type: ComponentType;
  format?: HeaderFormat;
  text?: string;
  mediaFile?: File;
  mediaPreview?: string;
  mediaUrl?: string;
  example?: {
    header_handle?: string[];
    body_text?: string[][];
  };
  buttons?: Array<{
    type: ButtonType;
    text: string;
    url?: string;
    phone_number?: string;
  }>;
}

const TemplateCreateModal: React.FC<TemplateCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editTemplate,
}) => {
  const [formData, setFormData] = useState({
    name: editTemplate?.name || '',
    language: editTemplate?.language || 'en',
    category: editTemplate?.category || 'MARKETING',
  });

  const [components, setComponents] = useState<TemplateComponent[]>([
    {
      type: 'BODY' as ComponentType,
      text: editTemplate?.bodyText || '',
    },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const textareaRefs = useRef<{ [key: number]: HTMLTextAreaElement | null }>({});

  if (!isOpen) return null;

  // Extract variables from text (e.g., {{1}}, {{2}})
  const extractVariables = (text: string): string[] => {
    const regex = /\{\{(\d+)\}\}/g;
    const matches = text.match(regex) || [];
    return Array.from(new Set(matches)).sort();
  };

  // Replace variables in text with test values
  const replaceVariables = (text: string): string => {
    return text.replace(/\{\{(\d+)\}\}/g, (match, number) => {
      return testVariables[number] || match;
    });
  };

  // Insert formatting at cursor position
  const insertFormatting = (componentIndex: number, formatType: 'bold' | 'italic' | 'strikethrough' | 'monospace') => {
    const textarea = textareaRefs.current[componentIndex];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    let formattedText = '';
    let cursorOffset = 0;

    switch (formatType) {
      case 'bold':
        formattedText = `*${selectedText}*`;
        cursorOffset = 1;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        cursorOffset = 1;
        break;
      case 'strikethrough':
        formattedText = `~${selectedText}~`;
        cursorOffset = 1;
        break;
      case 'monospace':
        formattedText = `\`\`\`${selectedText}\`\`\``;
        cursorOffset = 3;
        break;
    }

    const newText = text.substring(0, start) + formattedText + text.substring(end);
    updateComponent(componentIndex, { text: newText });

    // Restore cursor position
    setTimeout(() => {
      if (selectedText) {
        textarea.setSelectionRange(start + cursorOffset, start + cursorOffset + selectedText.length);
      } else {
        textarea.setSelectionRange(start + cursorOffset, start + cursorOffset);
      }
      textarea.focus();
    }, 0);
  };

  // Render formatted text for preview (convert WhatsApp formatting to HTML)
  const renderFormattedText = (text: string): JSX.Element => {
    // First replace variables
    let processedText = replaceVariables(text);

    // Convert WhatsApp formatting to HTML-like structure
    // Bold: *text* -> <strong>text</strong>
    // Italic: _text_ -> <em>text</em>
    // Strikethrough: ~text~ -> <s>text</s>
    // Monospace: ```text``` -> <code>text</code>

    const parts: Array<{ text: string; format?: string }> = [];
    let currentIndex = 0;

    // Regex to match formatting patterns
    const formatRegex = /(\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```)/g;
    let match;

    while ((match = formatRegex.exec(processedText)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push({ text: processedText.substring(currentIndex, match.index) });
      }

      const matchedText = match[0];
      let format = '';
      let content = '';

      if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
        format = 'bold';
        content = matchedText.slice(1, -1);
      } else if (matchedText.startsWith('_') && matchedText.endsWith('_')) {
        format = 'italic';
        content = matchedText.slice(1, -1);
      } else if (matchedText.startsWith('~') && matchedText.endsWith('~')) {
        format = 'strikethrough';
        content = matchedText.slice(1, -1);
      } else if (matchedText.startsWith('```') && matchedText.endsWith('```')) {
        format = 'monospace';
        content = matchedText.slice(3, -3);
      }

      parts.push({ text: content, format });
      currentIndex = match.index + matchedText.length;
    }

    // Add remaining text
    if (currentIndex < processedText.length) {
      parts.push({ text: processedText.substring(currentIndex) });
    }

    return (
      <>
        {parts.map((part, index) => {
          if (part.format === 'bold') {
            return <strong key={index}>{part.text}</strong>;
          } else if (part.format === 'italic') {
            return <em key={index}>{part.text}</em>;
          } else if (part.format === 'strikethrough') {
            return <s key={index}>{part.text}</s>;
          } else if (part.format === 'monospace') {
            return <code key={index} className="bg-gray-200 px-1 rounded text-xs">{part.text}</code>;
          } else {
            return <span key={index}>{part.text}</span>;
          }
        })}
      </>
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate template name (lowercase, underscores only, no spaces)
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Template name must be lowercase with underscores only (no spaces)';
    }

    // Validate body component exists
    const hasBody = components.some((c) => c.type === 'BODY');
    if (!hasBody) {
      newErrors.components = 'Template must have a BODY component';
    }

    // Validate each component
    components.forEach((component, index) => {
      if (component.type === 'HEADER' && component.format === 'TEXT' && !component.text?.trim()) {
        newErrors[`component_${index}`] = 'Header text is required';
      }
      if (component.type === 'BODY' && !component.text?.trim()) {
        newErrors[`component_${index}`] = 'Body text is required';
      }
      if (component.type === 'FOOTER' && !component.text?.trim()) {
        newErrors[`component_${index}`] = 'Footer text is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setErrors({});

    try {
      const headerComponent = components.find(c => c.type === 'HEADER');
      const bodyComponent = components.find(c => c.type === 'BODY');
      const footerComponent = components.find(c => c.type === 'FOOTER');
      const buttonsComponent = components.find(c => c.type === 'BUTTONS');

      // Media header (IMAGE/VIDEO/DOCUMENT): upload the picked file to the assets
      // bucket first, then pass its public URL + type so the backend can register
      // the media with Meta (header_handle) at create time.
      let headerType: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | undefined;
      let headerMediaUrl: string | undefined;
      if (headerComponent && headerComponent.format && headerComponent.format !== 'TEXT') {
        headerType = headerComponent.format as 'IMAGE' | 'VIDEO' | 'DOCUMENT';
        if (headerComponent.mediaUrl) {
          // Already uploaded (e.g. editing an existing template).
          headerMediaUrl = headerComponent.mediaUrl;
        } else if (headerComponent.mediaFile) {
          const upload = await whatsappService.uploadHeaderMedia(headerComponent.mediaFile);
          if (!upload.success || !upload.data?.url) {
            setErrors({ submit: upload.error || 'Failed to upload header media' });
            setSubmitting(false);
            return;
          }
          headerMediaUrl = upload.data.url;
        } else {
          setErrors({ submit: `Please upload ${headerType === 'DOCUMENT' ? 'a document' : headerType === 'VIDEO' ? 'a video' : 'an image'} for the header` });
          setSubmitting(false);
          return;
        }
      }

      const request: CreateTemplateRequest = {
        name: formData.name,
        language: formData.language,
        category: formData.category as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
        headerText: headerComponent?.format === 'TEXT' ? headerComponent.text : undefined,
        headerType,
        headerMediaUrl,
        bodyText: bodyComponent?.text || '',
        footerText: footerComponent?.text,
        buttonText: buttonsComponent?.buttons?.map(b => b.text).join(','),
      };

      const response = editTemplate?.id
        ? await whatsappService.updateTemplate(editTemplate.id, request)
        : await whatsappService.createTemplate(request);

      if (response.success && response.data) {
        onSuccess(response.data);
      } else {
        setErrors({ submit: response.error || (editTemplate ? 'Failed to update template' : 'Failed to create template') });
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'An unexpected error occurred' });
    } finally {
      setSubmitting(false);
    }
  };

  const addComponent = (type: ComponentType) => {
    const newComponent: TemplateComponent = {
      type,
      text: '',
    };

    if (type === 'HEADER') {
      newComponent.format = 'TEXT';
    }

    if (type === 'BUTTONS') {
      newComponent.buttons = [];
    }

    setComponents([...components, newComponent]);
  };

  const removeComponent = (index: number) => {
    setComponents(components.filter((_, i) => i !== index));
  };

  const updateComponent = (index: number, updates: Partial<TemplateComponent>) => {
    const updated = [...components];
    updated[index] = { ...updated[index], ...updates };
    setComponents(updated);
  };

  const handleMediaUpload = (index: number, file: File) => {
    const updated = [...components];

    // Validate file type
    const format = updated[index].format;
    let validTypes: string[] = [];

    if (format === 'IMAGE') {
      validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    } else if (format === 'VIDEO') {
      validTypes = ['video/mp4', 'video/3gpp'];
    } else if (format === 'DOCUMENT') {
      validTypes = ['application/pdf'];
    }

    if (!validTypes.includes(file.type)) {
      alert(`Invalid file type. Please upload ${format === 'IMAGE' ? 'JPEG/PNG' : format === 'VIDEO' ? 'MP4/3GP' : 'PDF'} files only.`);
      return;
    }

    // Validate file size (max 5MB for images, 16MB for videos, 100MB for documents)
    const maxSize = format === 'IMAGE' ? 5 * 1024 * 1024 : format === 'VIDEO' ? 16 * 1024 * 1024 : 100 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${format === 'IMAGE' ? '5MB' : format === 'VIDEO' ? '16MB' : '100MB'}`);
      return;
    }

    // Create preview URL
    const preview = URL.createObjectURL(file);

    updated[index] = {
      ...updated[index],
      mediaFile: file,
      mediaPreview: preview,
    };

    setComponents(updated);
  };

  const removeMedia = (index: number) => {
    const updated = [...components];
    if (updated[index].mediaPreview) {
      URL.revokeObjectURL(updated[index].mediaPreview!);
    }
    updated[index] = {
      ...updated[index],
      mediaFile: undefined,
      mediaPreview: undefined,
    };
    setComponents(updated);
  };

  const addButton = (componentIndex: number) => {
    const updated = [...components];
    if (!updated[componentIndex].buttons) {
      updated[componentIndex].buttons = [];
    }
    updated[componentIndex].buttons!.push({
      type: 'QUICK_REPLY',
      text: '',
    });
    setComponents(updated);
  };

  const removeButton = (componentIndex: number, buttonIndex: number) => {
    const updated = [...components];
    updated[componentIndex].buttons = updated[componentIndex].buttons?.filter(
      (_, i) => i !== buttonIndex
    );
    setComponents(updated);
  };

  const updateButton = (
    componentIndex: number,
    buttonIndex: number,
    updates: Partial<{
      type: ButtonType;
      text: string;
      url?: string;
      phone_number?: string;
    }>
  ) => {
    const updated = [...components];
    if (updated[componentIndex].buttons) {
      updated[componentIndex].buttons![buttonIndex] = {
        ...updated[componentIndex].buttons![buttonIndex],
        ...updates,
      };
    }
    setComponents(updated);
  };

  const hasComponentType = (type: ComponentType) => {
    return components.some((c) => c.type === type);
  };

  const renderPreview = () => {
    const headerComponent = components.find((c) => c.type === 'HEADER');
    const bodyComponent = components.find((c) => c.type === 'BODY');
    const footerComponent = components.find((c) => c.type === 'FOOTER');
    const buttonsComponent = components.find((c) => c.type === 'BUTTONS');

    // Extract variables from header and body text
    const headerVariables = headerComponent?.format === 'TEXT' && headerComponent.text
      ? extractVariables(headerComponent.text)
      : [];
    const bodyVariables = bodyComponent?.text ? extractVariables(bodyComponent.text) : [];
    const allVariables = Array.from(new Set([...headerVariables, ...bodyVariables])).sort();

    return (
      <div className="space-y-4">
        {/* Variable Test Inputs */}
        {allVariables.length > 0 && (
          <div className="bg-white border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Test Variables</h4>
            <p className="text-xs text-gray-600 mb-3">
              Enter sample values to preview how your message will look with dynamic content:
            </p>
            <div className="space-y-2">
              {allVariables.map((variable) => {
                const varNumber = variable.replace(/[{}]/g, '');
                return (
                  <div key={variable}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Variable {variable}
                    </label>
                    <input
                      type="text"
                      value={testVariables[varNumber] || ''}
                      onChange={(e) => {
                        setTestVariables({
                          ...testVariables,
                          [varNumber]: e.target.value,
                        });
                      }}
                      placeholder={`Example value for ${variable}`}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Message Preview */}
        <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
          <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-sm w-full shadow-lg">
            {headerComponent && (
              <div className="mb-3">
                {headerComponent.format === 'TEXT' && headerComponent.text && (
                  <h3 className="font-bold text-gray-900">
                    {replaceVariables(headerComponent.text)}
                  </h3>
                )}
                {headerComponent.format === 'IMAGE' && (
                  headerComponent.mediaPreview ? (
                    <img
                      src={headerComponent.mediaPreview}
                      alt="Header"
                      className="w-full h-32 object-cover rounded"
                    />
                  ) : (
                    <div className="bg-gray-200 rounded h-32 flex items-center justify-center text-gray-500 text-sm">
                      <div className="text-center">
                        <ImageIcon className="w-8 h-8 mx-auto mb-1 text-gray-400" />
                        <span>Upload an image</span>
                      </div>
                    </div>
                  )
                )}
                {headerComponent.format === 'VIDEO' && (
                  headerComponent.mediaPreview ? (
                    <video
                      src={headerComponent.mediaPreview}
                      controls
                      className="w-full h-32 object-cover rounded bg-black"
                    />
                  ) : (
                    <div className="bg-gray-200 rounded h-32 flex items-center justify-center text-gray-500 text-sm">
                      <div className="text-center">
                        <Video className="w-8 h-8 mx-auto mb-1 text-gray-400" />
                        <span>Upload a video</span>
                      </div>
                    </div>
                  )
                )}
                {headerComponent.format === 'DOCUMENT' && (
                  headerComponent.mediaFile ? (
                    <div className="bg-gray-100 rounded p-3 flex items-center gap-2">
                      <FileText className="w-6 h-6 text-blue-600" />
                      <span className="text-sm text-gray-700">{headerComponent.mediaFile.name}</span>
                    </div>
                  ) : (
                    <div className="bg-gray-200 rounded p-3 flex items-center justify-center text-gray-500 text-sm">
                      <div className="text-center">
                        <Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                        <span>Upload a document</span>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {bodyComponent && bodyComponent.text && (
              <div className="mb-3">
                <p className="text-gray-700 text-sm whitespace-pre-line">
                  {renderFormattedText(bodyComponent.text)}
                </p>
              </div>
            )}

            {footerComponent && footerComponent.text && (
              <div className="text-xs text-gray-500 italic mb-3">{footerComponent.text}</div>
            )}

            {buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0 && (
              <div className="border-t pt-2 space-y-1">
                {buttonsComponent.buttons.map((button, index) => (
                  <button
                    key={index}
                    className="w-full py-2 text-center text-blue-600 font-medium text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {button.text || 'Button'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {editTemplate ? 'Edit Template' : 'Create WhatsApp Template'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Form */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value.toLowerCase() })
                      }
                      placeholder="e.g., welcome_message"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={!!editTemplate}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Use lowercase letters, numbers, and underscores only
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language *
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="pt">Portuguese</option>
                      <option value="hi">Hindi</option>
                      <option value="ar">Arabic</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utility</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Marketing: Promotional messages | Utility: Account updates | Authentication:
                      OTPs & verification
                    </p>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Template Components</h3>
                </div>

                {errors.components && (
                  <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.components}
                  </p>
                )}

                {/* Add Component Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {!hasComponentType('HEADER') && (
                    <button
                      onClick={() => addComponent('HEADER')}
                      className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Header
                    </button>
                  )}
                  {!hasComponentType('BODY') && (
                    <button
                      onClick={() => addComponent('BODY')}
                      className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Body
                    </button>
                  )}
                  {!hasComponentType('FOOTER') && (
                    <button
                      onClick={() => addComponent('FOOTER')}
                      className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Footer
                    </button>
                  )}
                  {!hasComponentType('BUTTONS') && (
                    <button
                      onClick={() => addComponent('BUTTONS')}
                      className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Buttons
                    </button>
                  )}
                </div>

                {/* Component List */}
                <div className="space-y-4">
                  {components.map((component, index) => (
                    <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-semibold text-gray-700 uppercase">
                          {component.type}
                        </span>
                        {component.type !== 'BODY' && (
                          <button
                            onClick={() => removeComponent(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {component.type === 'HEADER' && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Header Format
                            </label>
                            <select
                              value={component.format}
                              onChange={(e) => {
                                updateComponent(index, {
                                  format: e.target.value as HeaderFormat,
                                  mediaFile: undefined,
                                  mediaPreview: undefined,
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="TEXT">Text</option>
                              <option value="IMAGE">Image</option>
                              <option value="VIDEO">Video</option>
                              <option value="DOCUMENT">Document</option>
                            </select>
                          </div>

                          {component.format === 'TEXT' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Header Text
                              </label>
                              <input
                                type="text"
                                value={component.text || ''}
                                onChange={(e) => updateComponent(index, { text: e.target.value })}
                                placeholder="Enter header text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                maxLength={60}
                              />
                              <p className="text-xs text-gray-500 mt-1">Max 60 characters</p>
                            </div>
                          )}

                          {component.format === 'IMAGE' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Header Image
                              </label>
                              {component.mediaPreview ? (
                                <div className="space-y-2">
                                  <div className="relative rounded-lg overflow-hidden border border-gray-300">
                                    <img
                                      src={component.mediaPreview}
                                      alt="Header preview"
                                      className="w-full h-40 object-cover"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeMedia(index)}
                                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {component.mediaFile?.name} ({(component.mediaFile!.size / 1024).toFixed(2)} KB)
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/jpg"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleMediaUpload(index, file);
                                    }}
                                    className="hidden"
                                    id={`image-upload-${index}`}
                                  />
                                  <label
                                    htmlFor={`image-upload-${index}`}
                                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
                                  >
                                    <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                                    <span className="text-sm text-gray-600">Click to upload image</span>
                                    <span className="text-xs text-gray-500 mt-1">JPEG, PNG (Max 5MB)</span>
                                  </label>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Recommended: 800x418px | Supported: JPEG, PNG | Max size: 5MB
                              </p>
                            </div>
                          )}

                          {component.format === 'VIDEO' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Header Video
                              </label>
                              {component.mediaPreview ? (
                                <div className="space-y-2">
                                  <div className="relative rounded-lg overflow-hidden border border-gray-300">
                                    <video
                                      src={component.mediaPreview}
                                      controls
                                      className="w-full h-40 object-cover bg-black"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeMedia(index)}
                                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {component.mediaFile?.name} ({(component.mediaFile!.size / 1024 / 1024).toFixed(2)} MB)
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <input
                                    type="file"
                                    accept="video/mp4,video/3gpp"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleMediaUpload(index, file);
                                    }}
                                    className="hidden"
                                    id={`video-upload-${index}`}
                                  />
                                  <label
                                    htmlFor={`video-upload-${index}`}
                                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
                                  >
                                    <Video className="w-8 h-8 text-gray-400 mb-2" />
                                    <span className="text-sm text-gray-600">Click to upload video</span>
                                    <span className="text-xs text-gray-500 mt-1">MP4, 3GP (Max 16MB)</span>
                                  </label>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Supported: MP4, 3GP | Max size: 16MB
                              </p>
                            </div>
                          )}

                          {component.format === 'DOCUMENT' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Header Document
                              </label>
                              {component.mediaFile ? (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg bg-gray-50">
                                    <FileText className="w-8 h-8 text-blue-600" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">{component.mediaFile.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {(component.mediaFile.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeMedia(index)}
                                      className="p-1 text-red-600 hover:text-red-800"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleMediaUpload(index, file);
                                    }}
                                    className="hidden"
                                    id={`document-upload-${index}`}
                                  />
                                  <label
                                    htmlFor={`document-upload-${index}`}
                                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
                                  >
                                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                    <span className="text-sm text-gray-600">Click to upload document</span>
                                    <span className="text-xs text-gray-500 mt-1">PDF (Max 100MB)</span>
                                  </label>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Supported: PDF | Max size: 100MB
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {component.type === 'BODY' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Body Text *
                          </label>

                          {/* Formatting Toolbar */}
                          <div className="flex gap-1 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <button
                              type="button"
                              onClick={() => insertFormatting(index, 'bold')}
                              className="p-2 hover:bg-gray-200 rounded transition-colors"
                              title="Bold (*text*)"
                            >
                              <Bold className="w-4 h-4 text-gray-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => insertFormatting(index, 'italic')}
                              className="p-2 hover:bg-gray-200 rounded transition-colors"
                              title="Italic (_text_)"
                            >
                              <Italic className="w-4 h-4 text-gray-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => insertFormatting(index, 'strikethrough')}
                              className="p-2 hover:bg-gray-200 rounded transition-colors"
                              title="Strikethrough (~text~)"
                            >
                              <Strikethrough className="w-4 h-4 text-gray-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => insertFormatting(index, 'monospace')}
                              className="p-2 hover:bg-gray-200 rounded transition-colors"
                              title="Monospace (```text```)"
                            >
                              <Code className="w-4 h-4 text-gray-700" />
                            </button>
                            <div className="flex-1" />
                            <div className="text-xs text-gray-500 self-center">
                              Select text and click to format
                            </div>
                          </div>

                          <textarea
                            ref={(el) => (textareaRefs.current[index] = el)}
                            value={component.text || ''}
                            onChange={(e) => updateComponent(index, { text: e.target.value })}
                            placeholder="Enter message body text. Use {{1}}, {{2}} for variables."
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                            maxLength={1024}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Max 1024 characters. Use {`{{1}}, {{2}}`} for dynamic content.
                          </p>
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                            <strong>Formatting:</strong> *bold*, _italic_, ~strikethrough~, ```monospace```
                          </div>
                          {errors[`component_${index}`] && (
                            <p className="text-xs text-red-600 mt-1">{errors[`component_${index}`]}</p>
                          )}
                        </div>
                      )}

                      {component.type === 'FOOTER' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Footer Text
                          </label>
                          <input
                            type="text"
                            value={component.text || ''}
                            onChange={(e) => updateComponent(index, { text: e.target.value })}
                            placeholder="e.g., Reply STOP to unsubscribe"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            maxLength={60}
                          />
                          <p className="text-xs text-gray-500 mt-1">Max 60 characters</p>
                        </div>
                      )}

                      {component.type === 'BUTTONS' && (
                        <div className="space-y-3">
                          {component.buttons?.map((button, btnIndex) => (
                            <div key={btnIndex} className="bg-gray-50 rounded p-3 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-gray-600">
                                  Button {btnIndex + 1}
                                </span>
                                <button
                                  onClick={() => removeButton(index, btnIndex)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Button Type
                                </label>
                                <select
                                  value={button.type}
                                  onChange={(e) =>
                                    updateButton(index, btnIndex, { type: e.target.value as ButtonType })
                                  }
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="QUICK_REPLY">Quick Reply</option>
                                  <option value="URL">Visit Website</option>
                                  <option value="CALL_TO_ACTION">Call Phone Number</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Button Text
                                </label>
                                <input
                                  type="text"
                                  value={button.text}
                                  onChange={(e) =>
                                    updateButton(index, btnIndex, { text: e.target.value })
                                  }
                                  placeholder="Button label"
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  maxLength={20}
                                />
                              </div>
                              {button.type === 'URL' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    URL
                                  </label>
                                  <input
                                    type="url"
                                    value={button.url || ''}
                                    onChange={(e) =>
                                      updateButton(index, btnIndex, { url: e.target.value })
                                    }
                                    placeholder="https://example.com"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              )}
                              {button.type === 'CALL_TO_ACTION' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Phone Number
                                  </label>
                                  <input
                                    type="tel"
                                    value={button.phone_number || ''}
                                    onChange={(e) =>
                                      updateButton(index, btnIndex, {
                                        phone_number: e.target.value,
                                      })
                                    }
                                    placeholder="+1234567890"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                          {(!component.buttons || component.buttons.length < 3) && (
                            <button
                              onClick={() => addButton(index)}
                              className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 text-sm font-medium"
                            >
                              + Add Button (Max 3)
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="lg:sticky lg:top-0 lg:self-start">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    {showPreview ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showPreview && renderPreview()}
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> After submission, templates must be approved by WhatsApp
                    before use. This typically takes 1-2 hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          {/* Error Message */}
          {errors.submit && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : editTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCreateModal;
